import { useState } from 'react';
import { Linkedin, Twitter, MessageCircle, Link2, Check, Share2, Users2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PublishToFeedDialog } from '@/components/community/PublishToFeedDialog';

// External share row — WhatsApp / X / LinkedIn / copy link, plus native share where available,
// and an in-app "Share to community feed" button that opens the composer prefilled.
//
// `hideCommunityShare` — omits the community CTA. Used on public / non-auth
// surfaces (e.g. the public event RSVP page for anonymous visitors) where
// tapping "Share to community" would take the user to a locked composer.
export const ShareEvent = ({ title, className = '', url: urlProp, embed = null, hideCommunityShare = false }) => {
  const [copied, setCopied] = useState(false);
  const [feedOpen, setFeedOpen] = useState(false);
  const url = urlProp || (typeof window !== 'undefined' ? window.location.href : '');
  const text = `Check out "${title}" on Bower`;
  const enc = encodeURIComponent;

  const links = [
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, href: `https://wa.me/?text=${enc(`${text} ${url}`)}` },
    { id: 'x', label: 'X', icon: Twitter, href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}` },
    { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
  ];

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { /* ignore */ }
  };
  const nativeShare = async () => {
    try { if (navigator.share) await navigator.share({ title, text, url }); else copy(); } catch { /* dismissed */ }
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="event-share">
      {navigator.share && (
        <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={nativeShare} data-testid="share-native"><Share2 className="h-3.5 w-3.5" /> Share</Button>
      )}
      {!hideCommunityShare && (
        <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={() => setFeedOpen(true)} data-testid="share-feed">
          <Users2 className="h-3.5 w-3.5" /> Share to community
        </Button>
      )}
      {links.map((l) => (
        <Button key={l.id} variant="outline" size="icon" className="rounded-full h-9 w-9" asChild data-testid={`share-${l.id}`} title={`Share on ${l.label}`}>
          <a href={l.href} target="_blank" rel="noopener noreferrer"><l.icon className="h-4 w-4" /></a>
        </Button>
      ))}
      <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={copy} data-testid="share-copy">
        {copied ? <><Check className="h-3.5 w-3.5 text-success" /> Copied</> : <><Link2 className="h-3.5 w-3.5" /> Copy link</>}
      </Button>
      <PublishToFeedDialog
        open={feedOpen}
        onOpenChange={setFeedOpen}
        title="Share this in B-Cafe?"
        description="Give your fellow founders a heads-up."
        defaultText={``}
        tag="Update"
        embed={embed}
        testid="event-share-feed-dialog"
      />
    </div>
  );
};

export default ShareEvent;
